import React from 'react';
import PropTypes from 'prop-types';
import {Field} from 'redux-form';
import {withApollo} from '@apollo/client/react/hoc';
import {isEmpty as _isEmpty} from 'lodash';

import {FiltersWrapper} from '@appComponents/HOCFiltersWrapper';
import Form from '@appComponents/ReduxFormControls';
import {
    EVENT_TYPE_NON_SPORTRADAR,
    EVENT_TYPE_SPORTRADAR,
    EVENT_TYPE_STAGE,
    HAS_FULL_COVERAGE,
    HAS_NO_COVERAGE,
    HAS_PARTIAL_COVERAGE,
} from '@constants/variables';
import {setTvChannelState} from '@modules/events/utils/eventContentHelper';
import {filteredSelectedTournament, filteredSelectedTournamentCategory} from '@modules/events/utils/eventSchedule';
import {showConstraintsModal} from '@modules/reports/utils/constraintsTables';
import {ContentVariantsFilter} from '@utils/commonFilters/ContentVariantsFilter';
import {DateRangeFilter} from '@utils/commonFilters/DateRangeFilter';
import {EventStatusFilter} from '@utils/commonFilters/EventStatusFilter';
import {EventTypeFilter} from '@utils/commonFilters/EventTypeFilter';
import {FirstStageCategoriesFilter} from '@utils/commonFilters/FirstStageCategoriesFilter';
import {ProductsFilter} from '@utils/commonFilters/ProductsFilter';
import {PropertyLicencesFilter} from '@utils/commonFilters/PropertyLicencesFilter';
import {SearchFilter} from '@utils/commonFilters/SearchFilter';
import {SecondStageCategoriesFilter} from '@utils/commonFilters/SecondStageCategoryFiter';
import {SportsFilter} from '@utils/commonFilters/SportsFilter';
import {StageSportsFilter} from '@utils/commonFilters/StageSportsFilter';
import {TournamentCategoriesFilter} from '@utils/commonFilters/TournamentCategoriesFilter';
import {TournamentsFilter} from '@utils/commonFilters/TournamentsFilter';
import {createCascadeFetchers} from '@utils/commonFilters/cascadeFetchers';
import {ContentCategoriesLevel1Filter} from '@utils/commonFilters/contentCategories/ContentCategoriesLevel1Filter';
import {ContentCategoriesLevel2Filter} from '@utils/commonFilters/contentCategories/ContentCategoriesLevel2Filter';
import {ContentCategoriesLevel3Filter} from '@utils/commonFilters/contentCategories/ContentCategoriesLevel3Filter';
import {
    addDaysToDate,
    formatDateWithoutSeconds,
    getEndOfTodayDate,
    getTodayDate,
    subtractDaysFromDate,
} from '@utils/date';
import {defineNextState} from '@utils/filters';
import {cancelGraphqlDebouncedFetches, createGraphqlDebouncedFetch} from '@utils/graphqlDebounce';
import {convertToInt} from '@utils/helpers';
import {mapPropertyLicencesForDropdown} from '@utils/propertyLicence';
import {sortByKey, sortDropdownOptionsAlphanumerically} from '@utils/sorters';

const MESSAGE_BOX_NAME = 'reportsMessage';

const REPORT_OPTIONS = [
    {
        key: HAS_NO_COVERAGE,
        text: 'None',
        value: HAS_NO_COVERAGE,
    },
    {
        key: HAS_PARTIAL_COVERAGE,
        text: 'Partial',
        value: HAS_PARTIAL_COVERAGE,
    },
    {
        key: HAS_FULL_COVERAGE,
        text: 'Full',
        value: HAS_FULL_COVERAGE,
    },
];

export class ReportsFilters extends React.Component {
    static propTypes = {
        client: PropTypes.object.isRequired,
        data: PropTypes.object,
        loading: PropTypes.bool.isRequired,
        filters: PropTypes.object.isRequired,
        form: PropTypes.shape({
            changeValue: PropTypes.func.isRequired,
        }).isRequired,
        restoreFilterValues: PropTypes.func.isRequired,
        restoreFilterDateValues: PropTypes.func.isRequired,
    };

    static defaultProps = {
        data: {},
    };

    static onSubmit = (data) => {
        const from = formatDateWithoutSeconds(data.from),
            to = formatDateWithoutSeconds(data.to);

        if (from !== data.from && to !== data.to) {
            data.from = getTodayDate();
            data.to = getEndOfTodayDate();
        } else if (from !== data.from) {
            data.from = subtractDaysFromDate(data.to, 1);
        } else if (to !== data.to) {
            data.to = addDaysToDate(data.from, 1);
        }

        showConstraintsModal(data);

        return data;
    };

    constructor(props) {
        super(props);

        this.state = {
            init: true,
            isSportradarEvent: false,
            isStageEvent: false,
            isNonSportradarEvent: false,
            hasSelectedSport: false,
            hasSportsWithTvChannel: false,
            hasSelectedCategoryLev1: false,
            hasSelectedCategoryLev2: false,
            hasSelectedTournamentCategory: false,
            hasSelectedTournaments: false,
            sports: [],
            fetchedSports: [],
            sportsLoading: false,
            fetchedStageSports: [],
            stageSportsLoading: false,
            tournamentCategories: [],
            tournamentCategoriesLoading: false,
            tournaments: [],
            tournamentsLoading: false,
            courts: [],
            courtsLoading: false,
            tvChannelsLoading: false,
            firstStageCategories: [],
            firstStageCategoriesLoading: false,
            secondStageCategories: [],
            secondStageCategoriesLoading: false,
            contentCategoriesFirstLevel: [],
            contentCategoriesFirstLevelLoading: false,
            contentCategoriesSecondLevel: [],
            contentCategoriesSecondLevelLoading: false,
            contentCategoriesThirdLevel: [],
            contentCategoriesThirdLevelLoading: false,
            selectedSports: [],
            selectedTournamentCategories: [],
            selectedTournaments: [],
            selectedCourts: [],
            selectedFirstStageCategories: [],
            selectedSecondStageCategories: [],
            selectedFirstLevelCategories: [],
            selectedSecondLevelCategories: [],
            selectedThirdLevelCategories: [],
            fromMaxDate: null,
            fromMaxDateDefault: null,
            fromMaxTime: null,
            toMinDate: null,
            toMinDateDefault: null,
            toMinTime: null,
        };
    }

    componentDidMount() {
        this.restoreFilterCascade();
    }

    componentDidUpdate(prevProps, prevState) {
        this.restoreFilterCascade();

        filteredSelectedTournamentCategory(this.state.selectedSports, prevState, this.setState.bind(this));
        filteredSelectedTournament(
            prevState,
            this.setState.bind(this),
            this.state.tournaments,
            this.state.selectedTournaments
        );
    }

    componentWillUnmount() {
        this.cancelDebouncedFetches();
    }

    setStateHandler = (stateObject) => {
        this.setState(() => (stateObject));
    };

    getEventTypeFlags = (eventType) => ({
        isSportradarEvent: EVENT_TYPE_SPORTRADAR === eventType,
        isStageEvent: EVENT_TYPE_STAGE === eventType,
        isNonSportradarEvent: EVENT_TYPE_NON_SPORTRADAR === eventType,
    });

    /**
     * Rebuilds the dependent dropdowns from the filters kept in redux / url params
     * so a reloaded or deep-linked page shows the same cascade the user submitted.
     */
    restoreFilterCascade = () => {
        const reportsFilters = this.props.filters?.reports;

        if (!reportsFilters || !this.props.form || !this.state.init) {
            return;
        }

        const {restoreFilterValues} = this.props,
            nextState = {},
            reduxEventType = restoreFilterValues(reportsFilters, 'eventType');

        if (reduxEventType) {
            const eventType = convertToInt(reduxEventType);

            Object.assign(nextState, this.getEventTypeFlags(eventType));

            if (EVENT_TYPE_SPORTRADAR === eventType && _isEmpty(this.state.fetchedSports)) {
                this.getStandardSportsFromGraphQL();
                nextState.sportsLoading = true;
            } else if (EVENT_TYPE_STAGE === eventType && _isEmpty(this.state.fetchedStageSports)) {
                this.getStageSportsFromGraphQL();
                nextState.stageSportsLoading = true;
            } else if (EVENT_TYPE_NON_SPORTRADAR === eventType && _isEmpty(this.state.contentCategoriesFirstLevel)) {
                this.getFirstLevelContentCategoriesFromGraphQL();
                nextState.contentCategoriesFirstLevelLoading = true;
            }
        }

        const reduxSports = restoreFilterValues(reportsFilters, 'sports');

        if (!_isEmpty(reduxSports)) {
            const sportIds = reduxSports.map(Number);

            nextState.hasSelectedSport = true;
            nextState.sports = sportIds;
            nextState.selectedSports = sportIds;

            if (_isEmpty(this.state.tournamentCategories)
                && (nextState.isSportradarEvent || this.state.isSportradarEvent)) {
                setTvChannelState(this.props.form, sportIds, this.setStateHandler, MESSAGE_BOX_NAME);
                this.fetchSportradarSportDependenciesDebounced(sportIds);

                const reduxTournamentCategories = restoreFilterValues(reportsFilters, 'tournamentCategories');

                if (!_isEmpty(reduxTournamentCategories)) {
                    nextState.hasSelectedTournamentCategory = true;

                    if (_isEmpty(this.state.tournaments)) {
                        this.fetchTournamentsDebounced(
                            reduxTournamentCategories.map((tournamentCategory) => convertToInt(tournamentCategory))
                        );

                        const reduxTournaments = restoreFilterValues(reportsFilters, 'tournaments');

                        if (!_isEmpty(reduxTournaments)) {
                            nextState.selectedTournaments = reduxTournaments;

                            const reduxCourts = restoreFilterValues(reportsFilters, 'court');

                            if (!_isEmpty(reduxCourts)) {
                                nextState.selectedCourts = reduxCourts;
                            }

                            this.loadCourtsForTournaments(sportIds, reduxTournaments);
                        }
                    }
                }
            } else if (_isEmpty(this.state.firstStageCategories)
                && (nextState.isStageEvent || this.state.isStageEvent)) {
                this.fetchFirstStageCategoriesDebounced(sportIds);

                const reduxFirstStageCategories = restoreFilterValues(reportsFilters, 'firstStageCategories');

                if (!_isEmpty(reduxFirstStageCategories)) {
                    nextState.selectedFirstStageCategories = reduxFirstStageCategories.map(Number);

                    const reduxSecondStageCategories = restoreFilterValues(
                        reportsFilters,
                        'secondStageCategories'
                    );

                    if (!_isEmpty(reduxSecondStageCategories)) {
                        nextState.selectedSecondStageCategories = reduxSecondStageCategories.map(Number);
                    }
                }
            }
        }

        const reduxContentCategoriesLvl1 = restoreFilterValues(reportsFilters, 'contentCategoriesLevel1');

        if (!_isEmpty(reduxContentCategoriesLvl1)) {
            nextState.hasSelectedCategoryLev1 = true;

            if (_isEmpty(this.state.contentCategoriesSecondLevel)) {
                this.getSecondLevelContentCategoriesDebounced(reduxContentCategoriesLvl1);

                const reduxContentCategoriesLvl2 = restoreFilterValues(reportsFilters, 'contentCategoriesLevel2');

                if (!_isEmpty(reduxContentCategoriesLvl2)) {
                    nextState.hasSelectedCategoryLev2 = true;

                    if (_isEmpty(this.state.contentCategoriesThirdLevel)) {
                        this.getThirdLevelContentCategoriesDebounced(reduxContentCategoriesLvl2);

                        const reduxContentCategoriesLvl3 = restoreFilterValues(
                            reportsFilters,
                            'contentCategoriesLevel3'
                        );

                        if (!_isEmpty(reduxContentCategoriesLvl3)) {
                            nextState.selectedThirdLevelCategories = reduxContentCategoriesLvl3;
                        }
                    }
                }
            }
        }

        nextState.init = false;

        this.setState(() => (nextState));
    };

    cancelDebouncedFetches = () => {
        cancelGraphqlDebouncedFetches([
            this.fetchSportradarSportDependenciesDebounced,
            this.fetchTournamentsDebounced,
            this.fetchCourtsDebounced,
            this.fetchFirstStageCategoriesDebounced,
            this.fetchSecondStageCategoriesDebounced,
            this.getSecondLevelContentCategoriesDebounced,
            this.getThirdLevelContentCategoriesDebounced,
        ]);
    };

    fetchSportradarSportDependenciesDebounced = createGraphqlDebouncedFetch((sportIds) => {
        this.getTournamentCategoriesFromGraphQL(sportIds);
    });

    fetchTournamentsDebounced = createGraphqlDebouncedFetch((tournamentCategoriesIds) => {
        this.getTournamentsFromGraphQL(tournamentCategoriesIds);
    });

    fetchCourtsDebounced = createGraphqlDebouncedFetch((tournamentIds) => {
        this.getCourtsFromGraphQL(tournamentIds);
    });

    fetchFirstStageCategoriesDebounced = createGraphqlDebouncedFetch((selectedSportIds) => {
        this.getFirstStageCategoriesFromGraphQL(selectedSportIds);
    });

    fetchSecondStageCategoriesDebounced = createGraphqlDebouncedFetch((selectedFirstStageCategoryIds) => {
        this.getSecondStageCategoriesFromGraphQL(selectedFirstStageCategoryIds);
    });

    getSecondLevelContentCategoriesDebounced = createGraphqlDebouncedFetch((firstLevelIds) => {
        this.getSecondLevelContentCategoriesFromGraphQL(firstLevelIds);
    });

    getThirdLevelContentCategoriesDebounced = createGraphqlDebouncedFetch((secondLevelIds) => {
        this.getThirdLevelContentCategoriesFromGraphQL(secondLevelIds);
    });

    cascadeFetchers = createCascadeFetchers({
        getClient: () => this.props.client,
        getForm: () => this.props.form,
        getState: () => this.state,
        setState: (updater) => this.setState(updater),
        messageBoxName: MESSAGE_BOX_NAME,
    });

    getStandardSportsFromGraphQL = (isStandard = true) => (
        this.cascadeFetchers.fetchStandardSports(isStandard)
    );

    getStageSportsFromGraphQL = (isStage = true) => (
        this.cascadeFetchers.fetchStageSports(isStage)
    );

    getTournamentCategoriesFromGraphQL = (sportIds) => (
        this.cascadeFetchers.fetchTournamentCategories(sportIds)
    );

    getTournamentsFromGraphQL = (tournamentCategoriesIds) => (
        this.cascadeFetchers.fetchTournaments(tournamentCategoriesIds)
    );

    getCourtsFromGraphQL = (tournamentIds) => (
        this.cascadeFetchers.fetchCourts(tournamentIds)
    );

    getFirstStageCategoriesFromGraphQL = (selectedSportIds) => (
        this.cascadeFetchers.fetchFirstStageCategories(selectedSportIds, {
            cancelPendingSecondStageFetch: () => this.fetchSecondStageCategoriesDebounced.cancel(),
            reloadSecondStageCategories: (ids) => this.getSecondStageCategoriesFromGraphQL(ids),
        })
    );

    getSecondStageCategoriesFromGraphQL = (selectedFirstStageCategoryIds) => (
        this.cascadeFetchers.fetchSecondStageCategories(selectedFirstStageCategoryIds)
    );

    getFirstLevelContentCategoriesFromGraphQL = () => (
        this.cascadeFetchers.fetchContentCategoriesLevel1()
    );

    getSecondLevelContentCategoriesFromGraphQL = (firstLevelIds) => (
        this.cascadeFetchers.fetchContentCategoriesLevel2(firstLevelIds)
    );

    getThirdLevelContentCategoriesFromGraphQL = (secondLevelIds) => (
        this.cascadeFetchers.fetchContentCategoriesLevel3(secondLevelIds)
    );

    onDateChange = (date, name) => {
        const nextState = {};

        defineNextState(nextState, this.state, date, name);

        this.setState(() => (nextState));
    };

    onEventTypeChange = (event, value) => {
        this.cancelDebouncedFetches();

        const eventTypeFlags = this.getEventTypeFlags(convertToInt(value));

        let nextState = {
            hasSelectedSport: false,
            hasSelectedTournamentCategory: false,
            hasSelectedCategoryLev1: false,
            hasSelectedCategoryLev2: false,
            ...eventTypeFlags,
        };

        if (eventTypeFlags.isSportradarEvent) {
            this.props.form.changeValue('contentCategoriesLevel1', null);
            this.props.form.changeValue('contentCategoriesLevel2', null);
            this.props.form.changeValue('contentCategoriesLevel3', null);
            this.props.form.changeValue('firstStageCategories', null);
            this.props.form.changeValue('secondStageCategories', null);
            this.props.form.changeValue('sports', null);
            nextState = {
                ...nextState,
                selectedFirstLevelCategories: [],
                selectedSecondLevelCategories: [],
                selectedThirdLevelCategories: [],
                selectedFirstStageCategories: [],
                selectedSecondStageCategories: [],
                firstStageCategories: [],
                secondStageCategories: [],
                selectedSports: [],
                sportsLoading: true,
            };
            this.getStandardSportsFromGraphQL();
        } else if (eventTypeFlags.isStageEvent) {
            this.props.form.changeValue('contentCategoriesLevel1', null);
            this.props.form.changeValue('contentCategoriesLevel2', null);
            this.props.form.changeValue('contentCategoriesLevel3', null);
            this.props.form.changeValue('tournamentCategories', null);
            this.props.form.changeValue('tournaments', null);
            this.props.form.changeValue('court', null);
            this.props.form.changeValue('sports', null);
            nextState = {
                ...nextState,
                selectedFirstLevelCategories: [],
                selectedSecondLevelCategories: [],
                selectedThirdLevelCategories: [],
                selectedTournaments: [],
                selectedTournamentCategories: [],
                tournamentCategories: [],
                tournaments: [],
                stageSportsLoading: true,
            };
            this.getStageSportsFromGraphQL();
        } else if (eventTypeFlags.isNonSportradarEvent) {
            this.props.form.changeValue('sports', null);
            this.props.form.changeValue('tournamentCategories', null);
            this.props.form.changeValue('tournaments', null);
            this.props.form.changeValue('court', null);
            this.props.form.changeValue('firstStageCategories', null);
            this.props.form.changeValue('secondStageCategories', null);
            nextState = {
                ...nextState,
                selectedSports: [],
                selectedTournaments: [],
                selectedTournamentCategories: [],
                selectedFirstStageCategories: [],
                selectedSecondStageCategories: [],
                firstStageCategories: [],
                secondStageCategories: [],
                contentCategoriesFirstLevelLoading: true,
            };
            this.getFirstLevelContentCategoriesFromGraphQL();
        } else {
            this.props.form.changeValue('contentCategoriesLevel1', null);
            this.props.form.changeValue('contentCategoriesLevel2', null);
            this.props.form.changeValue('contentCategoriesLevel3', null);
            this.props.form.changeValue('sports', null);
            this.props.form.changeValue('tournamentCategories', null);
            this.props.form.changeValue('tournaments', null);
            this.props.form.changeValue('court', null);
            this.props.form.changeValue('firstStageCategories', null);
            this.props.form.changeValue('secondStageCategories', null);
            nextState = {
                ...nextState,
                selectedSports: [],
                selectedTournaments: [],
                selectedTournamentCategories: [],
                selectedFirstLevelCategories: [],
                selectedSecondLevelCategories: [],
                selectedThirdLevelCategories: [],
                selectedFirstStageCategories: [],
                selectedSecondStageCategories: [],
                firstStageCategories: [],
                secondStageCategories: [],
            };
        }

        this.setState(() => (nextState));
    };

    onSportChange = (event, value) => {
        if (!this.state.isSportradarEvent) {
            return;
        }

        let nextState = {
            hasSelectedSport: false,
            hasSportsWithTvChannel: false,
            tournamentCategories: [],
            tournamentCategoriesLoading: false,
            tvChannelsLoading: false,
            selectedSports: [],
        };

        if (0 < value.length) {
            const sportIds = value.map(Number);

            nextState = {
                hasSelectedSport: true,
                tournamentCategoriesLoading: true,
                sports: sportIds,
                selectedSports: sportIds,
            };

            setTvChannelState(this.props.form, sportIds, this.setStateHandler, MESSAGE_BOX_NAME);
            this.fetchSportradarSportDependenciesDebounced(sportIds);
        } else {
            this.fetchSportradarSportDependenciesDebounced.cancel();
            this.fetchTournamentsDebounced.cancel();
            this.fetchCourtsDebounced.cancel();

            nextState = {
                ...nextState,
                selectedTournaments: [],
                selectedTournamentCategories: [],
                selectedCourts: [],
                hasSelectedTournaments: false,
                hasSelectedTournamentCategory: false,
                tournaments: [],
                courts: [],
            };

            this.props.form.changeValue('tournamentCategories', null);
            this.props.form.changeValue('tournaments', null);
            this.props.form.changeValue('court', null);
            this.props.form.changeValue('tvChannels', null);
        }

        this.setState(() => (nextState));
    };

    onStageSportChange = (event, value) => {
        if (!this.state.isStageEvent) {
            return;
        }

        const selectedSportIds = (value || []).map(Number);

        if (_isEmpty(selectedSportIds)) {
            this.fetchFirstStageCategoriesDebounced.cancel();
            this.fetchSecondStageCategoriesDebounced.cancel();

            this.setState(() => ({
                hasSelectedSport: false,
                selectedSports: [],
                firstStageCategories: [],
                firstStageCategoriesLoading: false,
                selectedFirstStageCategories: [],
                secondStageCategories: [],
                secondStageCategoriesLoading: false,
                selectedSecondStageCategories: [],
            }));
            this.props.form.changeValue('firstStageCategories', null);
            this.props.form.changeValue('secondStageCategories', null);

            return;
        }

        this.setState(
            () => ({
                hasSelectedSport: true,
                selectedSports: selectedSportIds,
                firstStageCategoriesLoading: true,
            }),
            () => {
                this.fetchSecondStageCategoriesDebounced.cancel();
                this.fetchFirstStageCategoriesDebounced(selectedSportIds);
            }
        );
    };

    onFirstStageCategoryChange = (event, value) => {
        if (_isEmpty(value)) {
            this.fetchSecondStageCategoriesDebounced.cancel();

            this.setState(() => ({
                selectedFirstStageCategories: [],
                secondStageCategories: [],
                secondStageCategoriesLoading: false,
                selectedSecondStageCategories: [],
            }));
            this.props.form.changeValue('secondStageCategories', null);

            return;
        }

        const selectedFirstStageCategoryIds = value.map(Number);

        this.setState(
            () => ({
                selectedFirstStageCategories: selectedFirstStageCategoryIds,
                secondStageCategoriesLoading: true,
            }),
            () => {
                this.fetchSecondStageCategoriesDebounced(selectedFirstStageCategoryIds);
            }
        );
    };

    onSecondStageCategoryChange = (event, value) => {
        this.setState(() => ({
            selectedSecondStageCategories: (value || []).map(Number),
        }));
    };

    onTournamentCategoryChange = (event, value) => {
        let nextState = {
            hasSelectedTournamentCategory: false,
            tournaments: [],
            tournamentsLoading: false,
        };

        if (0 < value.length) {
            nextState = {
                hasSelectedTournamentCategory: true,
                selectedTournamentCategories: value,
                tournamentsLoading: true,
            };

            this.fetchTournamentsDebounced(
                value.map((tournamentCategory) => convertToInt(tournamentCategory))
            );
        } else {
            this.fetchTournamentsDebounced.cancel();

            nextState = {
                ...nextState,
                selectedTournaments: [],
            };

            this.props.form.changeValue('tournaments', null);
        }

        this.setState(() => (nextState));
    };

    loadCourtsForTournaments = (sportIds, tournamentIds) => (
        this.cascadeFetchers.loadCourtsForTournaments(sportIds, tournamentIds, {
            scheduleCourtsFetch: (courtTournamentIds) => this.fetchCourtsDebounced(courtTournamentIds),
        })
    );

    onTournamentChange = (event, value) => {
        let nextState = {
            hasSelectedTournaments: false,
            courtsLoading: false,
            selectedTournaments: value,
        };

        if (0 < value.length) {
            this.loadCourtsForTournaments(this.state.sports, value);
        } else {
            this.fetchCourtsDebounced.cancel();

            nextState = {
                ...nextState,
                selectedCourts: [],
                courts: [],
            };

            this.props.form.changeValue('court', null);
        }

        this.setState(() => (nextState));
    };

    onCourtChange = (event, value) => {
        this.setState(() => ({
            selectedCourts: value,
        }));
    };

    onContentCategoryLevelFirstChange = (event, value) => {
        let nextState = {
            hasSelectedCategoryLev1: false,
            contentCategoriesSecondLevel: [],
            contentCategoriesSecondLevelLoading: false,
        };

        if (0 < value.length) {
            nextState = {
                hasSelectedCategoryLev1: true,
                selectedFirstLevelCategories: value,
                contentCategoriesSecondLevelLoading: true,
            };

            this.getSecondLevelContentCategoriesDebounced(
                value.map((contentCategory) => convertToInt(contentCategory))
            );
        } else {
            this.getSecondLevelContentCategoriesDebounced.cancel();
            this.getThirdLevelContentCategoriesDebounced.cancel();

            nextState = {
                ...nextState,
                selectedSecondLevelCategories: [],
                selectedThirdLevelCategories: [],
                contentCategoriesThirdLevel: [],
                contentCategoriesThirdLevelLoading: false,
                hasSelectedCategoryLev2: false,
            };

            this.props.form.changeValue('contentCategoriesLevel2', null);
            this.props.form.changeValue('contentCategoriesLevel3', null);
        }

        this.setState(() => (nextState));
    };

    onContentCategorySecondLevelChange = (event, value) => {
        let nextState = {
            hasSelectedCategoryLev2: false,
            contentCategoriesThirdLevel: [],
            contentCategoriesThirdLevelLoading: false,
        };

        if (0 < value.length) {
            nextState = {
                hasSelectedCategoryLev2: true,
                selectedSecondLevelCategories: value,
                contentCategoriesThirdLevelLoading: true,
            };

            this.getThirdLevelContentCategoriesDebounced(
                value.map((contentCategory) => convertToInt(contentCategory))
            );
        } else {
            nextState = {
                ...nextState,
                selectedThirdLevelCategories: [],
            };

            this.props.form.changeValue('contentCategoriesLevel3', null);
        }

        this.setState(() => (nextState));
    };

    onContentCategoryThirdLevelChange = (event, value) => {
        this.setState(() => ({
            selectedThirdLevelCategories: value,
        }));
    };

    render() {
        const {loading, restoreFilterValues, restoreFilterDateValues} = this.props,
            reportsFilters = this.props.filters.reports,
            propertyLicencesForDropdown = mapPropertyLicencesForDropdown(
                this.props.data.propertyLicencesForDropdown || []
            );

        const {
            broadcastStatuses = [],
            encodingDatacenters = [],
            eventContentVariants = [],
            eventStatuses = [],
            eventTypes = [],
            originStreamTypes = [],
            products = [],
            reportTypes = [],
            tvChannels = [],
        } = this.props.data;

        return (
            <div className='reports__filters filter__controls__container'>
                <SearchFilter
                    loading={loading}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                />
                <DateRangeFilter
                    loading={loading}
                    filters={reportsFilters}
                    restoreFilterDateValues={restoreFilterDateValues}
                    onChange={this.onDateChange}
                    dateConfig={{
                        fromMaxDate: this.state.fromMaxDate,
                        fromMaxDateDefault: this.state.fromMaxDateDefault,
                        fromMaxTime: this.state.fromMaxTime,
                        toMinDate: this.state.toMinDate,
                        toMinDateDefault: this.state.toMinDateDefault,
                        toMinTime: this.state.toMinTime,
                    }}
                />
                <PropertyLicencesFilter
                    propertyLicences={propertyLicencesForDropdown}
                    loading={loading}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                />
                <EventTypeFilter
                    eventTypes={[].concat(eventTypes.mapDataForDropdownWithIntVal()).sort(sortByKey('value'))}
                    loading={loading}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                    onChangeValue={this.onEventTypeChange}
                />
                <SportsFilter
                    sports={this.state.fetchedSports}
                    loading={this.state.sportsLoading || loading}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                    hidden={!this.state.isSportradarEvent}
                    onChangeValue={this.state.isSportradarEvent ? this.onSportChange : null}
                />
                <StageSportsFilter
                    stageSports={this.state.fetchedStageSports}
                    loading={this.state.stageSportsLoading || loading}
                    hidden={!this.state.isStageEvent}
                    onChangeValue={this.onStageSportChange}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                />
                <FirstStageCategoriesFilter
                    firstStageCategories={this.state.firstStageCategories}
                    loading={this.state.firstStageCategoriesLoading || loading}
                    hidden={!(this.state.isStageEvent && this.state.hasSelectedSport)}
                    onChangeValue={this.onFirstStageCategoryChange}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                />
                <SecondStageCategoriesFilter
                    secondStageCategories={this.state.secondStageCategories}
                    loading={this.state.secondStageCategoriesLoading || loading}
                    hidden={_isEmpty(this.state.selectedFirstStageCategories)}
                    filters={reportsFilters}
                    onChangeValue={this.onSecondStageCategoryChange}
                    restoreFilterValues={restoreFilterValues}
                />
                <TournamentCategoriesFilter
                    tournamentCategories={this.state.tournamentCategories}
                    loading={this.state.tournamentCategoriesLoading || loading}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                    hidden={!(this.state.isSportradarEvent && this.state.hasSelectedSport)}
                    onChangeValue={this.onTournamentCategoryChange}
                />
                <TournamentsFilter
                    tournaments={this.state.tournaments}
                    loading={this.state.tournamentsLoading || loading}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                    hidden={!this.state.hasSelectedTournamentCategory}
                    disabled={this.state.tournamentsLoading || this.state.tournamentCategoriesLoading || loading}
                    onChangeValue={this.onTournamentChange}
                />
                <Field
                    component={Form.ReduxSelect}
                    name='court'
                    hidden={!this.state.hasSelectedTournaments}
                    options={this.state.courts}
                    disabled={this.state.courtsLoading || loading}
                    placeholder='Venue / Court'
                    loading={this.state.courtsLoading || loading}
                    multiple
                    setValue={restoreFilterValues(reportsFilters, 'court')}
                    selection
                    onChangeValue={this.onCourtChange}
                    search
                />
                <ContentCategoriesLevel1Filter
                    loading={this.state.contentCategoriesFirstLevelLoading || loading}
                    hidden={!this.state.isNonSportradarEvent}
                    options={this.state.contentCategoriesFirstLevel}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                    onChangeValue={this.onContentCategoryLevelFirstChange}
                />
                <ContentCategoriesLevel2Filter
                    loading={this.state.contentCategoriesSecondLevelLoading || loading}
                    hidden={!this.state.hasSelectedCategoryLev1}
                    options={this.state.contentCategoriesSecondLevel}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                    onChangeValue={this.onContentCategorySecondLevelChange}
                />
                <ContentCategoriesLevel3Filter
                    loading={this.state.contentCategoriesThirdLevelLoading || loading}
                    hidden={!this.state.hasSelectedCategoryLev2}
                    options={this.state.contentCategoriesThirdLevel}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                    onChangeValue={this.onContentCategoryThirdLevelChange}
                />
                <Field
                    component={Form.ReduxSelect}
                    name='tvChannels'
                    hidden={!this.state.hasSportsWithTvChannel}
                    options={tvChannels.mapDataForDropdownWithIntVal()}
                    disabled={this.state.tvChannelsLoading || loading}
                    placeholder='TV channel'
                    loading={this.state.tvChannelsLoading || loading}
                    multiple
                    setValue={restoreFilterValues(reportsFilters, 'tvChannels')}
                    selection
                    search
                />
                <ContentVariantsFilter
                    eventContentVariants={sortDropdownOptionsAlphanumerically(
                        eventContentVariants.mapDataForDropdownWithIntVal()
                    )}
                    loading={loading}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                />
                <Field
                    className='--customWidth --originStreamType'
                    component={Form.ReduxSelect}
                    name='originStreamTypes'
                    placeholder='Entry point'
                    options={sortDropdownOptionsAlphanumerically(
                        originStreamTypes.mapDataForDropdownWithIntVal()
                    )}
                    search
                    loading={loading}
                    multiple
                    disabled={loading}
                    setValue={restoreFilterValues(reportsFilters, 'originStreamTypes')}
                />
                <Field
                    className='--customWidth --encodingDatacenter'
                    component={Form.ReduxSelect}
                    name='encodingDatacenter'
                    placeholder='Datacenter'
                    options={[].concat(encodingDatacenters.mapDataForDropdownWithIntVal()).sort(sortByKey('value'))}
                    search
                    loading={loading}
                    multiple
                    disabled={loading}
                    setValue={restoreFilterValues(reportsFilters, 'encodingDatacenter')}
                />
                <ProductsFilter
                    products={products.mapDataForDropdownWithIntVal()}
                    loading={loading}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                />
                <EventStatusFilter
                    eventStatuses={eventStatuses}
                    loading={loading}
                    filters={reportsFilters}
                    restoreFilterValues={restoreFilterValues}
                />
                <Field
                    className='--customWidth --report'
                    component={Form.ReduxSelect}
                    disabled={loading}
                    loading={loading}
                    multiple
                    name='report'
                    options={REPORT_OPTIONS}
                    placeholder='Report'
                    search
                    setValue={restoreFilterValues(reportsFilters, 'report')}
                />
                <Field
                    className='--customWidth --reportType'
                    component={Form.ReduxSelect}
                    disabled={loading}
                    loading={loading}
                    multiple
                    name='reportTypes'
                    options={sortDropdownOptionsAlphanumerically(
                        reportTypes.mapDataForDropdownWithIntVal()
                    )}
                    placeholder='Report type'
                    search
                    setValue={restoreFilterValues(reportsFilters, 'reportTypes')}
                />
                <Field
                    className='--customWidth --broadcastStatus'
                    component={Form.ReduxSelect}
                    name='broadcastStatus'
                    placeholder='Broadcast status'
                    options={sortDropdownOptionsAlphanumerically(
                        broadcastStatuses.mapDataForDropdownWithIntVal()
                    )}
                    search
                    loading={loading}
                    multiple
                    disabled={loading}
                    setValue={restoreFilterValues(reportsFilters, 'broadcastStatus')}
                />
            </div>
        );
    }
}

export default FiltersWrapper(withApollo(ReportsFilters), {
    name: 'reports',
    onSubmit: ReportsFilters.onSubmit,
    initialValues: {
        search: null,
        from: getTodayDate(),
        to: getEndOfTodayDate(),
    },
});
